import { useState, useEffect } from 'react';
import { useToast } from '@chakra-ui/react';
import axios from 'axios';

// Default (control) content — matches the original V1 page exactly.
// Text wrapped in *asterisks* renders with the blue accent in the headline
// and bold in the subheadline. Keep this in sync with what marketing sees
// as "Original" in the admin Split Test Manager.
export const DEFAULT_CONTENT = {
  headline: "You Have a *Software Idea.* We'll Turn It Into a Market-Ready Product in 180 Days — *Or You Don't Pay.*",
  subheadline: '*STEP 1:* Watch This Video (Sound On)',
  videoId: 'n5aciulrgw',
  ctaText: 'See If I Qualify',
  showVideo: true,
  showReviews: true,
  requireBusinessEmail: false,
  // Optional line under the headline (e.g. a guarantee). Blank = not rendered.
  tagline: '',
};

// Content for variants that predate config-driven content (config docs whose
// variants carry no `content` object). The historical V1/V2/V3 differed only
// by Wistia video.
export const LEGACY_VARIANT_CONTENT = {
  V1: DEFAULT_CONTENT,
  V2: { ...DEFAULT_CONTENT, videoId: 'rtsp5xbncn' },
  V3: { ...DEFAULT_CONTENT, videoId: 'slb2e4xkfk' },
};

// "*text*" markup → accent span. Odd-indexed pieces of the split are the
// starred segments.
export function renderAccented(text, accentClass) {
  if (!text) return null;
  return text.split(/\*([^*]+)\*/g).map((part, i) =>
    i % 2 === 1 ? <span key={i} className={accentClass}>{part}</span> : part
  );
}

export const stripAccents = (text) => (text || '').replace(/\*/g, '');

// Component to display reviews for the A/B test
const TrustpilotReviews = ({ onQualifyClick }) => (
  <div className="animate-fade-in mt-[10vh]">
      <h3 className="text-center text-xl font-bold text-gray-800 mb-4">See What Our Clients Have Built</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
           {/* Replace these with your actual review image URLs */}
          <img src="https://i.imgur.com/LnSuZUV.png" alt="Trustpilot Review 0" className="rounded-lg shadow-md" width="400" height="300" loading="lazy" />
          <img src="https://i.imgur.com/B0MDLjj.png" alt="Trustpilot Review 0" className="rounded-lg shadow-md" width="400" height="300" loading="lazy" />
          <img src="https://i.imgur.com/a1M4tFt.png" alt="Trustpilot Review 1" className="rounded-lg shadow-md" width="400" height="300" loading="lazy" />
          <img src="https://i.imgur.com/KN8oLP6.png" alt="Trustpilot Review 1" className="rounded-lg shadow-md" width="400" height="300" loading="lazy" />
          <img src="https://i.imgur.com/ufErcy1.png" alt="Trustpilot Review 1" className="rounded-lg shadow-md" width="400" height="300" loading="lazy" />
          <img src="https://i.imgur.com/puianWV.png" alt="Trustpilot Review 1" className="rounded-lg shadow-md" width="400" height="300" loading="lazy" />
          <img src="https://i.imgur.com/MbZmWSk.png" alt="Trustpilot Review 3" className="rounded-lg shadow-md" width="400" height="300" loading="lazy" />
          <img src="https://i.imgur.com/h0cMBzX.png" alt="Trustpilot Review 3" className="rounded-lg shadow-md" width="400" height="300" loading="lazy" />
          <img src="https://i.imgur.com/kASioVp.png" alt="Trustpilot Review 3" className="rounded-lg shadow-md" width="400" height="300" loading="lazy" />
          <img src="https://i.imgur.com/fjnDPrJ.png" alt="Trustpilot Review 3" className="rounded-lg shadow-md" width="400" height="300" loading="lazy" />
          <img src="https://i.imgur.com/RvLLGow.png" alt="Trustpilot Review 3" className="rounded-lg shadow-md" width="400" height="300" loading="lazy" />
          <img src="https://i.imgur.com/W76fvSp.png" alt="Trustpilot Review 3" className="rounded-lg shadow-md" width="400" height="300" loading="lazy" />
          <img src="https://i.imgur.com/nyNKD3Q.png" alt="Trustpilot Review 3" className="rounded-lg shadow-md" width="400" height="300" loading="lazy" />
          <img src="https://i.imgur.com/Xq84Yql.png" alt="Trustpilot Review 3" className="rounded-lg shadow-md" width="400" height="300" loading="lazy" />
          <img src="https://i.imgur.com/Ho9fSp2.png" alt="Trustpilot Review 3" className="rounded-lg shadow-md" width="400" height="300" loading="lazy" />
          <img src="https://i.imgur.com/OAcSTyY.png" alt="Trustpilot Review 3" className="rounded-lg shadow-md" width="400" height="300" loading="lazy" />
          <img src="https://i.imgur.com/r0SQ7vy.png" alt="Trustpilot Review 3" className="rounded-lg shadow-md" width="400" height="300" loading="lazy" />
          <img src="https://i.imgur.com/mczS7x9.png" alt="Trustpilot Review 2" className="rounded-lg shadow-md" width="400" height="300" loading="lazy" />


      </div>
      <div className="text-center mt-8">
        <button
          onClick={onQualifyClick}
          className="bg-[#2E5AF4] text-white font-bold py-3 px-8 rounded-lg hover:bg-blue-600 transition-colors"
        >
          See if you qualify
        </button>
      </div>
  </div>
);

const PrivacyDisclaimer = () => {
  return (
    <div className="text-xs text-gray-400 px-4 py-2 border-t border-gray-200 bg-gray-50">
      <p className="mb-2 mt-[20vh]">
        <strong>Privacy Notice:</strong> By submitting this form, you consent to Benmore Technologies
        collecting and using your personal information (name, email, phone) to:
      </p>
      <ul className="list-disc list-inside space-y-1 mb-2">
        <li>Contact you about our software development services</li>
        <li>Send you relevant marketing communications</li>
        <li>Create personalized advertising experiences</li>
      </ul>
      <p className="mb-2">
        We may share your information with trusted partners (including Meta/Facebook)
        to provide you with relevant ads and content. Your data is processed securely
        and you can opt out at any time.
      </p>
      <p>
        By clicking "Submit application," you agree to our{' '}
        <a href="https://www.benmore.tech/privacy-policy/" className="text-gray-600 underline">Privacy Policy</a> and{' '}
        <a href="https://www.benmore.tech/terms-of-service/" className="text-gray-600 underline">Terms of Service</a>.
        You can unsubscribe from marketing communications at any time.
      </p>
    </div>
  );
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const checkIfBusinessEmail = (email) => {
  const CONSUMER_DOMAINS = [
    'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com',
    'aol.com', 'msn.com', 'live.com', 'icloud.com',
    'protonmail.com', 'ymail.com', 'gmx.com'
  ];
  if (!email || !email.includes('@')) return false;
  const domain = email.toLowerCase().split('@')[1];
  return !CONSUMER_DOMAINS.includes(domain);
};

// lockVariant: standalone pages (not in the split test) always attribute to
// their own variantName, even if this browser was bucketed on /free-training.
// hideSteps: drop the "STEP 1" subheadline and the "STEP 2:" form-header prefix.
// angle: marketing angle sent with the lead (Slack line + Notion CRM), e.g. "Execution".
const LandingPage = ({ variantName = 'V1', content: contentOverrides, previewMode = false, muted = false, lockVariant = false, hideSteps = false, angle }) => {
  const toast = useToast();

  // Merge variant content over defaults, ignoring blank fields so a
  // half-filled variant in the admin never blanks out part of the page.
  const content = { ...DEFAULT_CONTENT };
  Object.entries(contentOverrides || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && String(v).trim() !== '') content[k] = v;
  });
  const videoId = content.videoId;

  // Check URL for hideVSL parameter (lazy initialization to prevent flash)
  const [hideVSLParam] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const hideVSLValue = params.get('hideVSL');
    return hideVSLValue === 'True' || hideVSLValue === 'true';
  });
  // Video can be hidden by URL param (legacy) or per-variant from the admin
  const hideVSL = hideVSLParam || content.showVideo === false;
  // Per-variant toggle from the admin: only business emails may submit
  const requireBusinessEmail = content.requireBusinessEmail === true;

  // Load Wistia scripts with error suppression (only if VSL is not hidden)
  useEffect(() => {
    // Skip loading Wistia if VSL is hidden
    if (hideVSL) return;
    // Add error handlers to suppress Wistia-related errors from browser extensions
    const handleError = (event) => {
      const errorMessage = event.message || '';
      const errorFilename = event.filename || '';

      if (
        errorMessage.includes('wistia') ||
        errorMessage.includes('Wistia') ||
        errorMessage.includes('app.wistia.com') ||
        errorMessage.includes('fast.wistia') ||
        errorFilename.includes('wistia') ||
        errorFilename.includes('publicApi.js') ||
        errorFilename.includes('videoThumbnail.js') ||
        errorFilename.includes('player.js') ||
        errorMessage.includes('Failed to fetch') ||
        errorMessage.includes('MediaError') ||
        errorMessage.includes('DEMUXER_ERROR') ||
        errorMessage.includes('playerBorderRadius') ||
        // Cross-origin Wistia scripts show as generic "Script error." with no details
        errorMessage === 'Script error.'
      ) {
        event.preventDefault();
        event.stopPropagation();
        return true;
      }
    };

    const handleUnhandledRejection = (event) => {
      const reason = event.reason ? event.reason.toString() : '';
      if (
        // Suppress undefined/null rejections — these come from Wistia's internal
        // autoplay handling when the browser blocks sound playback on mobile
        !event.reason ||
        reason.includes('wistia') ||
        reason.includes('Failed to fetch') ||
        reason.includes('publicApi') ||
        reason.includes('videoThumbnail') ||
        reason.includes('player.js') ||
        reason.includes('MediaError') ||
        reason.includes('DEMUXER_ERROR') ||
        reason.includes('playerBorderRadius')
      ) {
        event.preventDefault();
      }
    };

    window.addEventListener('error', handleError, true);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Load Wistia scripts
    const script1 = document.createElement('script');
    script1.src = 'https://fast.wistia.com/player.js';
    script1.async = true;
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.src = `https://fast.wistia.com/embed/${videoId}.js`;
    script2.async = true;
    script2.type = 'module';
    document.head.appendChild(script2);

    // Add Wistia styles
    const style = document.createElement('style');
    style.textContent = `
      wistia-player[media-id='${videoId}']:not(:defined) {
        background: center / contain no-repeat url('https://fast.wistia.com/embed/medias/${videoId}/swatch');
        display: block;
        filter: blur(5px);
        padding-top:56.25%;
      }
    `;
    document.head.appendChild(style);

    // Cleanup
    return () => {
      window.removeEventListener('error', handleError, true);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      try {
        if (script1.parentNode) document.head.removeChild(script1);
        if (script2.parentNode) document.head.removeChild(script2);
        if (style.parentNode) document.head.removeChild(style);
      } catch (error) {
        // Silently ignore cleanup errors
      }
    };
  }, [hideVSL, videoId]);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [investmentCapacity, setInvestmentCapacity] = useState('');
  const [planningTimeline, setPlanningTimeline] = useState('');
  const [developmentMethod, setDevelopmentMethod] = useState('');

  const [isNameValid, setIsNameValid] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isPhoneValid, setIsPhoneValid] = useState(false);

  const [isEmailVisible, setIsEmailVisible] = useState(false);
  const [isPhoneVisible, setIsPhoneVisible] = useState(false);
  const [isInvestmentVisible, setIsInvestmentVisible] = useState(false);
  const [isPlanningTimelineVisible, setIsPlanningTimelineVisible] = useState(false);
  const [isDevelopmentMethodVisible, setIsDevelopmentMethodVisible] = useState(false);

  const [isButtonActive, setIsButtonActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setIsNameValid(name.length >= 2);
    if (isNameValid) setIsEmailVisible(true);
  }, [name, isNameValid]);

  // Well-formed but personal (gmail, yahoo…) while the variant requires business
  const isPersonalEmailBlocked = requireBusinessEmail && EMAIL_PATTERN.test(email) && !checkIfBusinessEmail(email);

  useEffect(() => {
    setIsEmailValid(EMAIL_PATTERN.test(email) && !(requireBusinessEmail && !checkIfBusinessEmail(email)));
    if (isEmailValid) setIsPhoneVisible(true);
  }, [email, isEmailValid, requireBusinessEmail]);

  useEffect(() => {
    const phonePattern = /^(\+?1\s?)?(\([0-9]{3}\)|[0-9]{3})([.\-\s]?)?[0-9]{3}([.\-\s]?)?[0-9]{4}$/;
    setIsPhoneValid(phonePattern.test(phone));
    if (isPhoneValid) setIsInvestmentVisible(true);
  }, [phone, isPhoneValid]);

  useEffect(() => {
    const hasRequiredMethod = planningTimeline === 'I have already started working with a development team' ? developmentMethod !== '' : true;
    setIsButtonActive(isNameValid && isEmailValid && isPhoneValid && investmentCapacity !== '' && planningTimeline !== '' && hasRequiredMethod);
  }, [isNameValid, isEmailValid, isPhoneValid, investmentCapacity, planningTimeline, developmentMethod]);

  useEffect(() => {
    if (investmentCapacity !== '') {
      setIsPlanningTimelineVisible(true);
    }
  }, [investmentCapacity]);

  useEffect(() => {
    if (planningTimeline === 'I have already started working with a development team') {
      setIsDevelopmentMethodVisible(true);
    } else {
      setIsDevelopmentMethodVisible(false);
      setDevelopmentMethod('');
    }
  }, [planningTimeline]);

  const getCreativeID = () => {
    try {
        const params = new URLSearchParams(window.location.search);
        return params.get('creativeID');
    } catch (e) {
        // Fallback for environments without window.location
        return 'mockCreativeID';
    }
  };

  const getCampaignName = () => {
     try {
        const params = new URLSearchParams(window.location.search);
        return params.get('campaignName');
    } catch (e) {
        // Fallback for environments without window.location
        return 'mockCampaignName';
    }
  };

  const handleFormSubmit = (e) => {
      e.preventDefault();
      if (previewMode) {
        toast({
          title: 'Preview mode',
          description: 'The form is disabled in previews — no lead or conversion is recorded.',
          status: 'info',
          duration: 4000,
          isClosable: true,
        });
        return;
      }
      setIsSubmitting(true);

      if (isButtonActive) {
        const creativeID = getCreativeID();
        const campaignName = getCampaignName();

        axios.post('https://retainlysms-backend.herokuapp.com/funnel-form', {
          name,
          email,
          phone,
          investmentCapacity,
          planningTimeline,
          developmentMethod,
          creativeID: creativeID,
          campaignName: campaignName,
          // The headline this visitor actually saw — lands in the Notion CRM
          // "Landing Page Headline" field for per-lead attribution
          headlineText: stripAccents(content.headline),
          // Variant name attributes the conversion in split test stats
          vslVariant: lockVariant ? variantName : (localStorage.getItem('vsl_variant') || variantName),
          ...(angle && { angle }),
        })
        .then(response => {

          if (investmentCapacity === "No, less than $5,000" || investmentCapacity === "No, between $5,000 - $10,000") {
              // Use window.location.href to force page refresh and trigger tracking scripts
              window.location.href = '/dq';
          } else {
            // NEW PIXEL LOGIC: Only fire for Business emails + 10k-20k budget
            const isBusinessEmail = checkIfBusinessEmail(email);
            const isOptimalBudget = investmentCapacity === 'Yes, between $10,000 - $20,000' || investmentCapacity === 'Yes, over $20,000';

            if (isOptimalBudget) {
            // if (isBusinessEmail && isOptimalBudget) {
              if (window.fbq) {
                window.fbq('track', 'Lead');
              }
            }
            // Pass lead data to MQL page for Calendly prefill
            const mqlParams = new URLSearchParams({
              name,
              email,
              phone,
            });
            // Preserve product param if present
            const currentParams = new URLSearchParams(window.location.search);
            if (currentParams.get('product')) {
              mqlParams.set('product', currentParams.get('product'));
            }
            // Angle rides along so /mql can tag the Calendly booking and the
            // meeting-confirmation redirect shows angle-specific content
            if (angle) {
              mqlParams.set('angle', angle.toLowerCase());
            }
            // Use window.location.href to force page refresh and trigger tracking scripts
            window.location.href = `/mql?${mqlParams.toString()}`;
          }
        })
        .catch(error => {
          // 403 = blocklisted (IP/email/phone/name managed from /admin) —
          // keep the old client-side blocklist's exact messaging
          if (error?.response?.status === 403) {
            alert('Your request was not able to be processed.');
            return;
          }
          toast({
            title: "Error",
            description: "There was an issue submitting the form. Please try again.",
            status: "error",
            duration: 9000,
            isClosable: true,
          });
        })
        .finally(() => {
          setIsSubmitting(false);
        });


      } else {
        setIsSubmitting(false);
        toast({
          title: isPersonalEmailBlocked ? "Business email required" : "Almost there!",
          description: isPersonalEmailBlocked
            ? "Please use a business email to submit your application."
            : "Please finish filling out all fields before submitting.",
          status: "warning",
          duration: 5000,
          isClosable: true,
          position: "bottom",
        });
      }
  };


  return (
    <div className="flex px-2 justify-center h-screen py-4 ">

      <style>
        {`
          .shadow-custom {
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 10px 20px rgba(0, 0, 0, 0.1);
          }

          .animate-fade-in {
            animation: fade-in 0.5s ease-in-out;
          }
          @keyframes fade-in {
            0% { opacity: 0; transform: translateY(10px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          .blackSpinner {
            border: 4px solid rgba(255, 255, 255, 0.3);
            border-radius: 50%;
            border-top: 4px solid #ffffff;
            width: 24px;
            height: 24px;
            animation: spin 1s linear infinite;
            margin: auto;
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      <div className="w-full bg-white rounded-3xl">
        {/* Single background wrapper eliminates duplicate background-image fetch/paint */}
        <div style={{
          backgroundImage: 'url(https://cdn.prod.website-files.com/66da428db0e237a5894e0c53/66da428db0e237a5894e0cc1_desktop-hero-bgd.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}>
          <nav className="w-full px-2 flex justify-between items-center">
            <div className="flex items-center">
              <img
                src="https://cdn.prod.website-files.com/66da428db0e237a5894e0c53/66da428db0e237a5894e0cf3_new%20Benmore.svg"
                alt="Benmore Technologies"
                className="h-8 md:h-12"
                width="160"
                height="32"
              />
            </div>
          </nav>

          <div className="text-center px-6 pb-6">
            <h1 className="font-extrabold pt-6 text-2xl md:text-5xl md:px-[10%] leading-tight md:leading-tight">
              {renderAccented(content.headline, 'text-[#1D54E2]')}
            </h1>

            {content.tagline && (
              <h3 className="mt-3 text-lg md:text-2xl font-bold italic text-[#C8102E] md:px-[10%]">
                {renderAccented(content.tagline, 'underline')}
              </h3>
            )}

            {!hideSteps && (
              <h2 className="mt-4 text-lg md:text-2xl">
                {hideVSL
                  ? (<><span className='font-bold'>STEP 1:</span> See if you qualify below</>)
                  : renderAccented(content.subheadline, 'font-bold')}
              </h2>
            )}
          </div>
        </div>

        <form onSubmit={handleFormSubmit} className="space-y-6 p-6 pt-0" id="qualification-form">

          {!hideVSL && (
            <div className="text-center w-full md:w-3/5 mx-auto">
              {/* silent-autoplay="allow": if sound autoplay is blocked (mobile), fall back to muted autoplay.
                  muted: admin previews autoplay silently so open dashboards don't blast the VSL */}
              {muted
                ? <wistia-player key={`${videoId}-muted`} media-id={videoId} aspect="1.7777777777777777" autoplay muted playsinline></wistia-player>
                : <wistia-player key={videoId} media-id={videoId} aspect="1.7777777777777777" autoplay silent-autoplay="allow" playsinline></wistia-player>}
            </div>
          )}



            <div className='md:px-[10%]'>
            <h2 className="text-center my-4 pt-4 text-lg md:text-2xl" id="qualification-form-header">{!hideVSL && !hideSteps && <span className='font-bold'>STEP 2: </span>}See if you qualify for an <span className='whitespace-nowrap text-[#2E5AF4]'>NDA-covered</span> Strategy Call with our team</h2>

            <input
              type="text"
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full outline-[#2E5AF4] px-4 py-3 rounded-lg border-2"
            />

            {isEmailVisible && (
              <div>
                <input
                  type="email"
                  placeholder={requireBusinessEmail ? 'Business Email' : 'Email'}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full outline-[#2E5AF4] px-4 py-3 rounded-lg mt-4 border-2 ${isPersonalEmailBlocked ? 'border-red-400' : ''}`}
                />
                {isPersonalEmailBlocked && (
                  <p className="text-left text-sm text-red-500 mt-1">
                    Please use a business email — personal addresses (Gmail, Yahoo, etc.) aren't accepted.
                  </p>
                )}
              </div>
            )}

            {isPhoneVisible && (
              <input
                type="tel"
                placeholder="Phone Number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full outline-[#2E5AF4] px-4 py-3 rounded-lg mt-4 border-2"
              />
            )}

            {isInvestmentVisible && (
              <div className="mt-4">
                <p className="mb-2">Do you have a <strong>minimum of $10,000 - 20,000 USD in funds currently available</strong> to invest in developing your software application?</p>
                <div>
                  <label className="block mt-2">
                    <input
                      type="radio"
                      name="investmentCapacity"
                      value="Yes, over $20,000"
                      onChange={(e) => setInvestmentCapacity(e.target.value)}
                      className="mr-2"
                    />
                    Yes, over $20,000
                  </label>
                  <label className="block mt-2">
                    <input
                      type="radio"
                      name="investmentCapacity"
                      value="Yes, between $10,000 - $20,000"
                      onChange={(e) => setInvestmentCapacity(e.target.value)}
                      className="mr-2"
                    />
                    Yes, between $10,000 - $20,000
                  </label>
                  <label className="block mt-2">
                    <input
                      type="radio"
                      name="investmentCapacity"
                      value="No, between $5,000 - $10,000"
                      onChange={(e) => setInvestmentCapacity(e.target.value)}
                      className="mr-2"
                    />
                    No, between $5,000 - $10,000
                  </label>
                  <label className="block mt-2">
                    <input
                      type="radio"
                      name="investmentCapacity"
                      value="No, less than $5,000"
                      onChange={(e) => setInvestmentCapacity(e.target.value)}
                      className="mr-2"
                    />
                    No, less than $5,000
                  </label>
                </div>
              </div>
            )}

            {isPlanningTimelineVisible && (
              <div className="mt-6">
                <p className="mb-2 font-bold">When do you plan to start working with a development team?</p>
                <div>
                  <label className="block mt-2">
                    <input
                      type="radio"
                      name="planningTimeline"
                      value="I have already started working with a development team"
                      onChange={(e) => setPlanningTimeline(e.target.value)}
                      className="mr-2"
                    />
                    I have already started working with a development team
                  </label>
                  <label className="block mt-2">
                    <input
                      type="radio"
                      name="planningTimeline"
                      value="Now"
                      onChange={(e) => setPlanningTimeline(e.target.value)}
                      className="mr-2"
                    />
                    Now
                  </label>
                  <label className="block mt-2">
                    <input
                      type="radio"
                      name="planningTimeline"
                      value="Within 30 days"
                      onChange={(e) => setPlanningTimeline(e.target.value)}
                      className="mr-2"
                    />
                    Within 30 days
                  </label>
                  <label className="block mt-2">
                    <input
                      type="radio"
                      name="planningTimeline"
                      value="30 days or more"
                      onChange={(e) => setPlanningTimeline(e.target.value)}
                      className="mr-2"
                    />
                    30 days or more
                  </label>
                </div>
              </div>
            )}

            {isDevelopmentMethodVisible && (
              <div className="mt-6">
                <p className="mb-2 font-bold">Did you use AI tools to build your software yourself or did you hire a team to build it for you?</p>
                <div>
                  <label className="block mt-2">
                    <input
                      type="radio"
                      name="developmentMethod"
                      value="Used AI tools myself"
                      onChange={(e) => setDevelopmentMethod(e.target.value)}
                      className="mr-2"
                    />
                    Used AI tools to build it myself
                  </label>
                  <label className="block mt-2">
                    <input
                      type="radio"
                      name="developmentMethod"
                      value="Hired a team"
                      onChange={(e) => setDevelopmentMethod(e.target.value)}
                      className="mr-2"
                    />
                    Hired a team to build it
                  </label>
                  <label className="block mt-2">
                    <input
                      type="radio"
                      name="developmentMethod"
                      value="Not started yet"
                      onChange={(e) => setDevelopmentMethod(e.target.value)}
                      className="mr-2"
                    />
                    I have not started building yet
                  </label>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 mt-6 text-white font-bold rounded-lg bg-[#2E5AF4]"
            >
              {isSubmitting ? (
                <div className="blackSpinner"></div>
              ) : (
                <p>{stripAccents(content.ctaText)}</p>
              )}
            </button>

            {content.showReviews !== false && (
              <TrustpilotReviews
                onQualifyClick={() => {
                  document.getElementById('qualification-form-header')?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                  });
                }}
              />
            )}

          </div>
        </form>
        <PrivacyDisclaimer />
      </div>
    </div>
  );
};

export default LandingPage;
